import { useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { DataTable, Field, Modal, ReasonModal, StatusBadge } from './ui.jsx';
import { StatTile } from './charts.jsx';
import { useFetch } from '../lib/hooks.js';
import { formatDate, formatXaf } from '../lib/format.js';
import { useMeta } from '../state/MetaContext.jsx';
import { useToast } from '../state/ToastContext.jsx';

/*
 * The main meter's own account with the utility: what ENEO (or CAMWATER)
 * charged - from the main meter readings - against what the landlord has paid
 * them, and so what is still owed. Every payment carries the utility's receipt
 * and goes into the money log.
 */

const today = () => new Date().toISOString().slice(0, 10);
const monthName = (period) =>
  period ? new Date(`${period}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';

/** Charged by the utility, paid to it, still owed: the three figures, for the dashboards. */
export const MainMeterSummary = ({ utility, charged, paid, owed, ahead = 0 }) => (
  <div className="stat-row money-summary">
    <StatTile value={formatXaf(charged)} label={`Charged by ${utility}`} note="Main meter bills" />
    <StatTile value={formatXaf(paid)} label={`Paid to ${utility}`} note={ahead > 0 ? `${formatXaf(ahead)} paid ahead` : 'With receipts'} />
    <StatTile
      value={formatXaf(owed)}
      label={`Still owed to ${utility}`}
      note={owed > 0 ? 'Not paid yet' : 'All paid'}
      tone={owed > 0 ? 'warn' : undefined}
    />
  </div>
);

const PayUtilityModal = ({ service, months, owed, onDone, onClose }) => {
  const toast = useToast();
  const { paymentMethods } = useMeta();
  const firstOpen = months.find((month) => month.paid < month.charged);
  const [form, setForm] = useState({
    readingId: firstOpen ? String(firstOpen.readingId) : '',
    amount: String(firstOpen ? firstOpen.charged - firstOpen.paid : owed || ''),
    method: 'mtn_momo',
    reference: '',
    paidOn: today(),
    note: ''
  });
  const [proof, setProof] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const pickMonth = (readingId) => {
    const month = months.find((item) => String(item.readingId) === readingId);
    set({ readingId, ...(month ? { amount: String(Math.max(0, month.charged - month.paid)) } : {}) });
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const body = new FormData();
      for (const [key, value] of Object.entries(form)) if (value !== '') body.append(key, value);
      body.append('proof', proof);
      await api.upload(`/landlord/utility-services/${service.id}/main-payments`, body);
      toast.success(`Payment to ${service.name} recorded.`);
      onDone();
    } catch (error) {
      if (error instanceof ApiError && error.details) setErrors(error.details);
      else toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Record a payment to ${service.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="pay-utility-form" className="btn btn-primary" disabled={busy || !proof || !(Number(form.amount) > 0)}>
            {busy ? 'Saving...' : 'Record the payment'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        What you paid {service.name} for the main meter. {owed > 0 ? `${formatXaf(owed)} is still owed to them.` : ''}
      </p>
      <form id="pay-utility-form" className="stack" onSubmit={submit}>
        <Field label="For the bill of" error={errors.readingId}>
          <select value={form.readingId} onChange={(event) => pickMonth(event.target.value)}>
            <option value="">Not one month in particular</option>
            {months.map((month) => (
              <option key={month.readingId} value={month.readingId}>
                {monthName(month.period)} - {formatXaf(month.charged)}
                {month.paid ? `, ${formatXaf(month.paid)} paid` : ''}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid-2">
          <Field label="Amount paid (FCFA)" error={errors.amount} required>
            <input type="number" min="1" value={form.amount} onChange={(event) => set({ amount: event.target.value })} required />
          </Field>
          <Field label="Paid on" error={errors.paidOn} required>
            <input type="date" max={today()} value={form.paidOn} onChange={(event) => set({ paidOn: event.target.value })} required />
          </Field>
        </div>
        <div className="grid-2">
          <Field label="Paid by" error={errors.method} required>
            <select value={form.method} onChange={(event) => set({ method: event.target.value })}>
              {paymentMethods.map((method) => (
                <option key={method.slug} value={method.slug}>
                  {method.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reference" hint={`The ${service.name} receipt or transaction number.`} error={errors.reference}>
            <input type="text" value={form.reference} onChange={(event) => set({ reference: event.target.value })} />
          </Field>
        </div>
        <Field label={`${service.name} receipt`} hint={proof ? `Attached: ${proof.name}` : 'A photo of the receipt, or the payment screenshot. Required.'} required>
          <input type="file" accept="image/*,application/pdf" onChange={(event) => setProof(event.target.files?.[0] || null)} required />
        </Field>
        <Field label="Note" hint="Optional." error={errors.note}>
          <input type="text" value={form.note} onChange={(event) => set({ note: event.target.value })} />
        </Field>
      </form>
    </Modal>
  );
};

/** On a metered service: the main meter's bills from the utility and what has been paid on them. */
export const MainMeterPayments = ({ service, canEdit = true, onChanged = () => {} }) => {
  const toast = useToast();
  const data = useFetch(`/landlord/utility-services/${service.id}/main-payments`, [service.id]);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(null);
  if (!data.data) return null;
  const { totals, months, items } = data.data;
  const reload = () => {
    data.reload();
    onChanged();
  };

  return (
    <section className="section-block">
      <div className="section-head">
        <h2>Paying {service.name} for the main meter</h2>
        {canEdit ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setPaying(true)} disabled={!months.length}>
            Record a payment to {service.name}
          </button>
        ) : null}
      </div>
      {!months.length ? (
        <p className="muted">Record a main meter reading with what {service.name} charged, and its payments can be tracked here.</p>
      ) : null}

      <MainMeterSummary utility={service.name} {...totals} />

      {months.length ? (
        <DataTable
          columns={[
            { key: 'month', header: 'Bill for', render: (month) => monthName(month.period) },
            { key: 'charged', header: `${service.name} charged`, render: (month) => formatXaf(month.charged) },
            { key: 'paid', header: 'Paid', render: (month) => formatXaf(month.paid) },
            {
              key: 'owed',
              header: 'Still owed',
              render: (month) =>
                month.charged > month.paid ? (
                  <strong className="owing">{formatXaf(month.charged - month.paid)}</strong>
                ) : (
                  <StatusBadge status="approved" label="Paid" />
                )
            }
          ]}
          rows={months}
          rowKey={(month) => month.readingId}
        />
      ) : null}

      {items.length ? (
        <ul className="refund-list main-payment-list">
          {items.map((payment) => (
            <li key={payment.id} className={payment.voided ? 'is-void' : ''}>
              <div>
                <strong>{formatXaf(payment.amount)} paid</strong>
                <span className="muted">
                  {' '}
                  {formatDate(payment.paidOn)} - {payment.method.replace(/_/g, ' ')}
                  {payment.reference ? ` - ref ${payment.reference}` : ''}
                  {payment.period ? ` - for ${monthName(payment.period)}` : ''}
                  {payment.recordedBy ? ` - recorded by ${payment.recordedBy}` : ''}
                </span>
                {payment.voided ? <span className="muted"> Cancelled: {payment.voidReason}</span> : null}
              </div>
              <div className="cell-actions">
                {payment.voided ? <StatusBadge status="withdrawn" label="Cancelled" /> : null}
                {payment.proofUrl ? (
                  <a href={payment.proofUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                    Receipt
                  </a>
                ) : null}
                {canEdit && !payment.voided ? (
                  <button type="button" className="link-btn" onClick={() => setVoiding(payment)}>
                    Cancel
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {paying ? (
        <PayUtilityModal
          service={service}
          months={months}
          owed={totals.owed}
          onDone={() => {
            setPaying(false);
            reload();
          }}
          onClose={() => setPaying(false)}
        />
      ) : null}
      {voiding ? (
        <ReasonModal
          title={`Cancel the ${formatXaf(voiding.amount)} payment to ${service.name}?`}
          lead="Use this only if it was entered by mistake. It stays on the books marked as cancelled, and counts as unpaid again."
          label="Why is it being cancelled?"
          confirmLabel="Cancel the payment"
          tone="danger"
          onSubmit={async (reason) => {
            await api.post(`/landlord/utility-main-payments/${voiding.id}/void`, { reason });
            toast.success('Payment cancelled.');
            setVoiding(null);
            reload();
          }}
          onClose={() => setVoiding(null)}
        />
      ) : null}
    </section>
  );
};
